import React from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { RootState, RootDispatch } from "../../../store";
import {
  fetchWarehousesThunk,
  createWarehouseThunk,
  updateWarehouseThunk,
} from "../../../store/slices/warehouseSlice";
import {
  IWarehouseRecord,
  WarehouseFormData,
} from "../../../models/warehouse.model";
import Select from "react-select";

interface WarehouseModalProps {
  isOpen: boolean;
  onClose: () => void;
  editMode: boolean;
  selectedItem: IWarehouseRecord | null;
  warehouseForm: Partial<WarehouseFormData>;
  setWarehouseForm: React.Dispatch<
    React.SetStateAction<Partial<WarehouseFormData>>
  >;
  warehousePage: number;
  itemsPerPage: number;
  searchQuery: string;
}

const Button: React.FC<{
  type: string;
  onClick: (e?: React.FormEvent) => void | Promise<void>;
  className: string;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ type, onClick, className, disabled = false, children }) => (
  <button
    type={type as "button" | "submit"}
    onClick={onClick}
    disabled={disabled}
    className={`px-4 py-2 rounded-md font-medium transition-colors flex items-center justify-center min-w-[100px]
      ${disabled ? "cursor-not-allowed opacity-70" : ""}
      ${className}`}
  >
    {children}
  </button>
);

const WarehouseModal: React.FC<WarehouseModalProps> = ({
  isOpen,
  onClose,
  editMode,
  selectedItem,
  warehouseForm,
  setWarehouseForm,
  warehousePage,
  itemsPerPage,
  searchQuery,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const { allUsersNames } = useSelector((state: RootState) => state.users);
  const { loading } = useSelector((state: RootState) => state.warehouse);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setWarehouseForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selected: { _id: string } | null) => {
    setWarehouseForm((prev) => ({
      ...prev,
      contactPerson: selected?._id || "",
    }));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    try {
      if (
        !warehouseForm.name ||
        !warehouseForm.address ||
        !warehouseForm.contactPerson
      ) {
        toast.error("Please fill all required fields.");
        return;
      }
      if (editMode && selectedItem) {
        await dispatch(
          updateWarehouseThunk({
            id: selectedItem._id,
            data: warehouseForm,
          }),
        ).unwrap();
      } else {
        await dispatch(
          createWarehouseThunk(warehouseForm as WarehouseFormData),
        ).unwrap();
      }
      await dispatch(
        fetchWarehousesThunk({
          page: warehousePage,
          limit: itemsPerPage,
          search: searchQuery,
        }),
      );
      onClose();
    } catch (err: any) {
      toast.error(
        err?.message || `Failed to ${editMode ? "update" : "create"} warehouse`,
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-scale-in">
        <div className="bg-gray-100 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">
            {editMode ? "Edit" : "Add"} Factory
          </h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 focus:outline-none"
            aria-label="Close modal"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={warehouseForm.name || ""}
                  onChange={handleChange}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Address Line 2
                </label>
                <input
                  type="text"
                  name="addressLine2"
                  value={warehouseForm.addressLine2 || ""}
                  onChange={handleChange}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  GSTIN
                </label>
                <input
                  type="text"
                  name="gstin"
                  value={warehouseForm.gstin || ""}
                  onChange={handleChange}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  value={warehouseForm.city || ""}
                  onChange={handleChange}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Pincode
                </label>
                <input
                  type="text"
                  name="pincode"
                  value={warehouseForm.pincode || ""}
                  onChange={handleChange}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  State Code
                </label>
                <input
                  type="text"
                  name="stateCode"
                  value={warehouseForm.stateCode || ""}
                  onChange={handleChange}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Address *
                </label>
                <input
                  type="text"
                  name="address"
                  value={warehouseForm.address || ""}
                  onChange={handleChange}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Contact Person *
                </label>
                <Select
                  value={allUsersNames.find(
                    (user) => user._id === warehouseForm.contactPerson,
                  )}
                  onChange={handleSelectChange}
                  options={allUsersNames}
                  getOptionLabel={(option) => option.username}
                  getOptionValue={(option) => option._id}
                  className="w-full"
                  classNamePrefix="react-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderColor: "#cbd5e0",
                      "&:hover": { borderColor: "#0077C0" },
                      boxShadow: "none",
                      borderRadius: "0.5rem",
                      padding: "0.25rem",
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999,
                      borderRadius: "0.5rem",
                    }),
                    option: (base, { isFocused, isSelected }) => ({
                      ...base,
                      backgroundColor: isSelected
                        ? "#0077C0"
                        : isFocused
                          ? "#f1f5f9"
                          : "white",
                      color: isSelected ? "white" : "#1f2937",
                      "&:hover": { backgroundColor: "#f1f5f9" },
                    }),
                  }}
                  isSearchable
                  isClearable
                  required
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="submit"
                onClick={handleSubmit}
                disabled={loading}
                className={`bg-[#0077C0] text-white ${
                  loading ? "hover:bg-[#0077C0]" : "hover:bg-[#005B90]"
                }`}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : editMode ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WarehouseModal;
